import React from "react";
import { ArrowLeft } from "lucide-react";

import { getSingleAssetComparisonSeries, getTagSummary } from "../../api/client";
import resultStyles from "../single-asset-agent-dev/singleAssetResult.module.css";
import { holdingTotalCost } from "../asset-overview/holdingSummaryModel";
import { openHoldingPage } from "../asset-overview/openHolding";
import { TagPerformanceCard } from "./TagPerformanceCard";
import { TagPnlCalendar } from "./TagPnlCalendar";
import styles from "./tagSummary.module.css";

const BENCHMARK_ID = "cn.index.000300.SH";

function missing(value) {
  return value === null || value === undefined || value === "";
}

function num(value) {
  return missing(value) ? null : Number(value);
}

function fmt(value, digits = 2) {
  const parsed = num(value);
  return parsed === null || !Number.isFinite(parsed)
    ? "--"
    : parsed.toLocaleString("zh-CN", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function signedMoney(value) {
  const parsed = num(value);
  if (parsed === null || !Number.isFinite(parsed)) return "--";
  return `${parsed > 0 ? "+" : ""}${fmt(parsed)}`;
}

function pct(value) {
  const parsed = num(value);
  return parsed === null || !Number.isFinite(parsed) ? "--" : `${parsed > 0 ? "+" : ""}${(parsed * 100).toFixed(2)}%`;
}

// 概览行里 today_pct_change 是「百分点」（12.3 表示 12.3%），关注行 today_change 是小数。
function pctPoint(value) {
  const parsed = num(value);
  return parsed === null || !Number.isFinite(parsed) ? "--" : `${parsed > 0 ? "+" : ""}${parsed.toFixed(2)}%`;
}

function tone(value) {
  const parsed = num(value);
  if (parsed === null || !Number.isFinite(parsed) || parsed === 0) return "";
  return parsed > 0 ? styles.up : styles.down;
}

function resultTone(value) {
  const parsed = num(value);
  if (parsed === null || !Number.isFinite(parsed) || parsed === 0) return "";
  return parsed > 0 ? resultStyles.gain : resultStyles.loss;
}

function sortRows(rows, sort, valueFor) {
  const direction = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((left, right) => {
    const a = Number(valueFor(left, sort.key));
    const b = Number(valueFor(right, sort.key));
    if (!Number.isFinite(a) && !Number.isFinite(b)) return 0;
    if (!Number.isFinite(a)) return 1;
    if (!Number.isFinite(b)) return -1;
    return (a - b) * direction;
  });
}

function SortTh({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  const direction = active ? (sort.direction === "asc" ? "升序" : "降序") : "未排序";
  return (
    <th>
      <button type="button" className={styles.sortButton} onClick={() => onSort(sortKey)} aria-label={`按${label}排序，当前${direction}`}>
        {label} <span aria-hidden="true">{active && sort.direction === "asc" ? "↑" : "↓"}</span>
      </button>
    </th>
  );
}

// 「价格 | 涨跌」「总盈亏 | %」两格里的两个数各自可排序（同概览 AC-105）。
function SplitSortTh({ first, second, sort, onSort }) {
  const button = ([label, sortKey]) => {
    const active = sort.key === sortKey;
    const direction = active ? (sort.direction === "asc" ? "升序" : "降序") : "未排序";
    return (
      <button type="button" className={styles.sortButton} onClick={() => onSort(sortKey)} aria-label={`按${label}排序，当前${direction}`}>
        {label} <span aria-hidden="true">{active && sort.direction === "asc" ? "↑" : "↓"}</span>
      </button>
    );
  };
  return (
    <th>
      <span className={styles.splitHead}>{button(first)}<i>|</i>{button(second)}</span>
    </th>
  );
}

function useSort(initial) {
  const [sort, setSort] = React.useState(initial);
  const toggle = React.useCallback((key) => setSort((current) => ({
    key, direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
  })), []);
  return [sort, toggle];
}

function AssetCell({ item, onOpen }) {
  return (
    <button type="button" className={styles.assetLink} onClick={onOpen} title={item.asset_name || item.asset_code}>
      <span>{item.asset_name || item.asset_code}</span>
      <small>{item.asset_code}</small>
    </button>
  );
}

const HOLDING_VALUE = {
  price: (row) => row.metrics?.latest_price,
  pct: (row) => row.metrics?.today_pct_change,
  ret: (row) => row.metrics?.book_total_return ?? row.metrics?.total_return,
  today: (row) => row.metrics?.today_pnl,
  qty: (row) => row.metrics?.quantity,
  avg: (row) => row.metrics?.cost_per_share,
  cost: (row) => holdingTotalCost(row.metrics ?? {}),
  mv: (row) => row.metrics?.market_value,
  unreal: (row) => {
    const cost = num(holdingTotalCost(row.metrics ?? {}));
    const value = num(row.metrics?.market_value);
    return cost === null || value === null ? null : value - cost;
  },
  real: (row) => row.metrics?.realized_pnl,
  div: (row) => row.metrics?.dividend_income,
  profit: (row) => row.metrics?.total_profit,
  ann: (row) => row.annual_return,
};

function HoldingsTable({ rows, onOpen }) {
  const [sort, toggle] = useSort({ key: "mv", direction: "desc" });
  const sorted = sortRows(rows, sort, (row, key) => HOLDING_VALUE[key](row));
  const th = (label, key) => <SortTh key={key} label={label} sortKey={key} sort={sort} onSort={toggle} />;
  return (
    <section className={styles.section} aria-label="持仓明细">
      <header className={styles.sectionHead}><strong>持仓明细</strong><small>共 {rows.length} 只</small></header>
      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>标的</th>
              <SplitSortTh first={["价格", "price"]} second={["涨跌", "pct"]} sort={sort} onSort={toggle} />{th("今日盈亏", "today")}{th("持股数", "qty")}{th("成本价", "avg")}
              {th("总成本", "cost")}{th("持仓市值", "mv")}{th("持仓收益", "unreal")}{th("已实现收益", "real")}
              {th("累计分红", "div")}<SplitSortTh first={["总盈亏", "profit"]} second={["%", "ret"]} sort={sort} onSort={toggle} />{th("年化收益率", "ann")}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const m = row.metrics ?? {};
              return (
                <tr key={row.asset_code}>
                  <td><AssetCell item={row} onOpen={() => onOpen(row)} /></td>
                  <td><b>{fmt(m.latest_price)}</b><small className={tone(m.today_pct_change)}>{pctPoint(m.today_pct_change)}</small></td>
                  <td className={tone(m.today_pnl)}>{signedMoney(m.today_pnl)}</td>
                  <td>{fmt(m.quantity, 0)}</td>
                  <td>{fmt(m.cost_per_share)}</td>
                  <td>{fmt(HOLDING_VALUE.cost(row))}</td>
                  <td>{fmt(m.market_value)}</td>
                  <td className={tone(HOLDING_VALUE.unreal(row))}>{signedMoney(HOLDING_VALUE.unreal(row))}</td>
                  <td className={tone(m.realized_pnl)}>{signedMoney(m.realized_pnl)}</td>
                  <td>{fmt(m.dividend_income)}</td>
                  <td className={tone(m.total_profit)}><b>{signedMoney(m.total_profit)}</b><small>{pct(m.book_total_return ?? m.total_return)}</small></td>
                  <td className={tone(row.annual_return)}>{pct(row.annual_return)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const WATCH_VALUE = (row, key) => (row.market ?? {})[key];

function WatchesTable({ rows, onOpen, hasSummary }) {
  const [sort, toggle] = useSort({ key: null, direction: "desc" });
  const sorted = sort.key ? sortRows(rows, sort, WATCH_VALUE) : rows;
  const th = (label, key) => <SortTh key={key} label={label} sortKey={key} sort={sort} onSort={toggle} />;
  return (
    <section className={styles.section} aria-label="关注列表">
      <header className={styles.sectionHead}><strong>关注列表</strong>{hasSummary && <small>不计入上面的数字</small>}</header>
      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>标的</th>
              {th("价格 | 涨跌", "today_change")}{th("开盘", "open")}{th("最高", "high")}{th("最低", "low")}
              {th("成交额", "amount")}{th("流通市值", "circ_mv")}{th("PE", "pe")}{th("PB", "pb")}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const m = row.market ?? {};
              return (
                <tr key={row.asset_code}>
                  <td><AssetCell item={row} onOpen={() => onOpen(row)} /></td>
                  <td><b>{fmt(m.latest_price)}</b><small className={tone(m.today_change)}>{pct(m.today_change)}</small></td>
                  <td>{fmt(m.open)}</td>
                  <td>{fmt(m.high)}</td>
                  <td>{fmt(m.low)}</td>
                  {/* stock_daily.amount 单位是千元、total_mv 单位是万元，同概览关注表 */}
                  <td>{num(m.amount) === null ? "--" : `${fmt(num(m.amount) / 100000)} 亿`}</td>
                  <td>{num(m.circ_mv) === null ? "--" : `${fmt(num(m.circ_mv) / 10000)} 亿`}</td>
                  <td>{fmt(m.pe)}</td>
                  <td>{fmt(m.pb)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// 三组指标条：样式复用核算页（AC-107）。
function MetricStrip({ summary }) {
  return (
    <section className={resultStyles.accountingSummary} aria-label="持仓与收益指标">
      <div className={resultStyles.holdingGroup}>
        <div className={resultStyles.compactMetric}><span>总成本</span><strong>{fmt(summary.total_cost)}</strong></div>
        <div className={resultStyles.compactMetric}><span>持仓市值</span><strong>{fmt(summary.market_value)}</strong></div>
      </div>
      <div className={resultStyles.profitGroup}>
        <div className={resultStyles.totalProfit}><span>总盈亏</span><strong className={resultTone(summary.total_profit)}>{signedMoney(summary.total_profit)}</strong></div>
        <div className={resultStyles.profitBreakdown}>
          <div><span>持仓收益</span><strong className={resultTone(summary.unrealized_pnl)}>{signedMoney(summary.unrealized_pnl)}</strong></div>
          <div><span>已实现收益</span><strong className={resultTone(summary.realized_pnl)}>{signedMoney(summary.realized_pnl)}</strong></div>
          <div><span>累计分红</span><strong className={resultTone(summary.dividend_income)}>{signedMoney(summary.dividend_income)}</strong></div>
        </div>
      </div>
      <div className={resultStyles.returnGroup}>
        <div className={resultStyles.compactMetric}><span>盈亏比例</span><strong className={resultTone(summary.book_total_return)}>{pct(summary.book_total_return)}</strong></div>
        <div className={resultStyles.compactMetric}><span>年化收益率</span><strong className={resultTone(summary.annual_return)}>{pct(summary.annual_return)}</strong></div>
      </div>
    </section>
  );
}

export function TagSummaryPage({ tagName, onBack, navigate }) {
  const [state, setState] = React.useState({ status: "loading", data: null, error: "" });
  const [benchmark, setBenchmark] = React.useState([]);

  React.useEffect(() => {
    let active = true;
    setState({ status: "loading", data: null, error: "" });
    setBenchmark([]);
    getTagSummary(tagName)
      .then((data) => { if (active) setState({ status: "ready", data, error: "" }); })
      .catch((error) => { if (active) setState({ status: "error", data: null, error: error.message || "标签汇总暂时无法加载。" }); });
    return () => { active = false; };
  }, [tagName]);

  const hasSeries = (state.data?.series ?? []).length > 0;
  React.useEffect(() => {
    if (!hasSeries) return undefined;
    let active = true;
    // 沪深300 只是对比线，取不到就只画标签线，不打断页面。
    getSingleAssetComparisonSeries({ instrumentIds: [BENCHMARK_ID] })
      .then((payload) => {
        const points = payload?.data?.series?.[0]?.points ?? [];  // 接口外层是 {meta, data: {series}}
        if (active) setBenchmark(points.map((point) => ({ date: point.t, value: point.raw })));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [hasSeries, tagName]);

  const [openError, setOpenError] = React.useState("");
  // 和概览 / 添加关注页同一条路：持仓打开核算页，关注打开行情页；「返回」回到这个标签页。
  // 不能走 /market 行情页：它只认股票和指数，点场外基金 / ETF 会报「没有找到该标的」。
  const openAsset = async (row) => {
    const returnTo = `/tag/${encodeURIComponent(tagName)}`;
    if (!row.holding_id) {
      navigate(`/single?asset=${encodeURIComponent(row.asset_code)}`, { state: { returnTo } });
      return;
    }
    try {
      await openHoldingPage(navigate, row, { returnTo });
    } catch (error) {
      setOpenError(error?.message || "暂时无法打开该持仓。");
    }
  };
  const data = state.data;
  const summary = data?.summary ?? {};
  const watchOnly = Boolean(data) && data.holdings_count === 0 && data.watches_count > 0;

  return (
    <main className={styles.page} id="main-content">
      <div className={styles.content}>
      <div className={`${resultStyles.resultSurface} ${styles.stack}`}>
        {openError && <div className={styles.state} role="alert">{openError}</div>}
        <header className={styles.head}>
          <div className={styles.title}>
            <button className={resultStyles.resultBackButton} type="button" onClick={onBack} aria-label="返回概览" title="返回概览">
              <ArrowLeft size={18} />
            </button>
            <h1 title={tagName}>{tagName}</h1>
            <span className={resultStyles.badge}>投资组合</span>
          </div>
          {hasSeries && (
            <div className={styles.today}>
              <span>今日盈亏</span>
              <strong className={resultTone(summary.today_pnl)}>{signedMoney(summary.today_pnl)}</strong>
              <small className={resultTone(summary.today_pct)}>{pct(summary.today_pct)}</small>
            </div>
          )}
        </header>

        {state.status === "loading" && <div className={styles.state} role="status">正在加载标签汇总…</div>}
        {state.status === "error" && <div className={styles.state} role="alert">{state.error}</div>}

        {data && (
          <>
            {!watchOnly && <MetricStrip summary={summary} />}
            {data.holdings.length > 0 && <HoldingsTable rows={data.holdings} onOpen={openAsset} />}
            {hasSeries ? (
              <div className={styles.charts}>
                <TagPerformanceCard series={data.series} benchmark={benchmark} />
                <TagPnlCalendar series={data.series} />
              </div>
            ) : !watchOnly && (  // 只有关注标的的标签：没有收益数字和图表可说，指标条和提示都不放，直接是关注列表
              <div className={`${styles.section} ${styles.note}`}>
                {data.holdings_count > 0
                  ? "这个标签下的持仓暂时算不出收益（还没有第二个交易日的行情），有数据后自动出现。"
                  : "这个标签下还没有持仓或关注。"}
              </div>
            )}
            {data.watches.length > 0 && <WatchesTable rows={data.watches} onOpen={openAsset} hasSummary={!watchOnly} />}
          </>
        )}
      </div>
      </div>
    </main>
  );
}
